export type ReadResult = {
  name: string;
  path: string;
  sha?: string;
  content?: string; // base64
  encoding?: string;
  download_url?: string;
  type?: string;
  size?: number;
};

function getAdminKey(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("adminKey");
}

function encodeBase64Utf8(input: string): string {
  // btoa expects Latin1; convert UTF-8 safely
  return btoa(unescape(encodeURIComponent(input)));
}

export async function contentRead(path: string, branch?: string): Promise<ReadResult> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const branchQuery = branch ? `&branch=${encodeURIComponent(branch)}` : "";
  const res = await fetch(
    `/api/content/read?path=${encodeURIComponent(path)}${branchQuery}`,
    {
      headers: { "x-admin-key": key, Authorization: `Bearer ${key}` },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Read failed (${res.status})`);
  }
  return (await res.json()) as ReadResult;
}

export async function contentList(path: string): Promise<{
  items: Array<{ name: string; path: string; type: string; size?: number }>;
}> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const res = await fetch(
    `/api/content/list?path=${encodeURIComponent(path)}`,
    {
      headers: { "x-admin-key": key },
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `List failed (${res.status})`);
  return data;
}

export async function contentExists(path: string): Promise<boolean> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const res = await fetch(
    `/api/content/exists?path=${encodeURIComponent(path)}`,
    {
      headers: { "x-admin-key": key },
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Exists failed (${res.status})`);
  return Boolean(data?.exists);
}

export async function contentUpload(
  file: File,
  destPath: string,
  branch?: string
): Promise<{ ok: boolean; path: string; size: number }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const form = new FormData();
  form.append("file", file);
  form.append("dest", destPath);
  if (branch) form.append("branch", branch);
  const res = await fetch("/api/content/upload", {
    method: "POST",
    headers: { "x-admin-key": key, Authorization: `Bearer ${key}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Upload failed (${res.status})`);
  return data;
}

export async function contentRemove(
  path: string,
  message?: string
): Promise<{ ok: boolean; path: string }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const res = await fetch("/api/content/remove", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-key": key },
    body: JSON.stringify({ path, message }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Remove failed (${res.status})`);
  return data;
}

export async function contentWrite(
  path: string,
  textContent: string,
  message?: string,
  branch?: string
): Promise<{ ok: boolean; path: string; commit?: string | null }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const body: any = {
    path,
    contentBase64: encodeBase64Utf8(textContent),
    message,
  };
  if (branch) body.branch = branch;
  const res = await fetch("/api/content/github", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-admin-key": key,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Write failed (${res.status})`);
  return data as any;
}

export async function contentBatchWrite(
  files: Array<{ path: string; textContent: string }>,
  message?: string,
  branch?: string
): Promise<{ ok: boolean; commit?: string | null }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const payload: any = {
    files: files.map((f) => ({
      path: f.path,
      contentBase64: encodeBase64Utf8(f.textContent),
    })),
    message,
  };
  if (branch) payload.branch = branch;
  const res = await fetch("/api/content/batch", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-admin-key": key,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Batch failed (${res.status})`);
  return data as any;
}

export async function contentDelete(
  path: string,
  message?: string,
  branch?: string
): Promise<{ ok: boolean; path: string }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const body: any = { path, message };
  if (branch) body.branch = branch;
  const res = await fetch("/api/content/github", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "x-admin-key": key,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Delete failed (${res.status})`);
  return data as any;
}

export async function contentMove(
  src: string,
  dest: string,
  branch?: string
): Promise<{ ok: boolean; moved: number }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const body: any = { src, dest };
  if (branch) body.branch = branch;
  const res = await fetch("/api/content/move", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-key": key },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Move failed (${res.status})`);
  return data;
}

export async function createPreviewPR(
  headBranch: string,
  title?: string,
  description?: string
): Promise<{ ok: boolean; number: number; url: string }> {
  const key = getAdminKey();
  if (!key) throw new Error("Admin key not set");
  const res = await fetch("/api/content/pr", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-key": key },
    body: JSON.stringify({ head: headBranch, title, body: description }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `PR failed (${res.status})`);
  return data;
}
